import React, { useContext } from 'react'
import { ArticleBreach } from 'component-library/user-registration/breach/ArticleBreach'
import { EntitledToView } from 'component-library/user-registration/entitlements/EntitledToView'

import { ArticleHero } from 'component-library/publication/ArticleHero/ArticleHero'
import { ArticleBlockContent } from 'component-library/publication/ArticleBlockContent/ArticleBlockContent'
import { Headline } from 'component-library/publication/Headline/Headline'
import { Byline } from 'component-library/publication/Byline/Byline'
import { Footnote } from 'component-library/publication/Footnote/Footnote'

import {
    StyledArticleContent,
    StyledGrid,
    StyledCell,
    StyledNestedGrid,
    StyledSharing,
    StyledAside,
    StyledStickyCell,
    StyledStickyTop,
    StyledFlexCell,
} from 'component-library/templates/Publication/TheWestPublication/TheWestPublication.styled'

import { getCanonicalLink } from 'component-library/templates/Publication/lib/get-canonical-link'
import { PublicationProps } from 'component-library/templates/Publication/Publication.props'

import { useFeature } from 'common/feature-togglings/features'
import { RelatedContent } from 'component-library/content/RelatedContent/RelatedContent'
import { LinkBanner } from 'component-library/banners/LinkBanner/LinkBanner'
import { RippleAd } from 'component-library/advertising/Ripple/Ripple'
import { ConfigurationContext } from 'common/context-providers/configuration-context'
import { useSectionMeta } from 'common/routing/use-section-meta'
import { getBaseUrl } from 'common/routing/url'
import { FeatureToggle } from 'component-library/feature-toggling/Toggle/Toggle'

export const TheWestPublication: React.FC<PublicationProps> = props => {
    const publication = props.data
    const content = publication.content
    const canonicalLink = getCanonicalLink(publication)
    const heroImage = publication.heroImage
    const { publicUrl } = useContext(ConfigurationContext)
    const sectionMeta = useSectionMeta()
    const articleUrl = `${getBaseUrl(publicUrl, sectionMeta)}/${publication.primaryTopic.id}/${
        publication.slug
    }`

    // isPremium is only avaliable on the ArticlePublication interface.
    const isPremium = publication.kind === 'article' ? publication.isPremium : false

    const enableMyHome = useFeature('my-home')

    const relatedItems = props.relatedPublications && props.relatedPublications.items
    const userTopicItems = props.userTopicPublications && props.userTopicPublications.items

    const renderUserTopicPublications = enableMyHome && userTopicItems && (
        <RelatedContent title="My Topics" items={userTopicItems} onEvent={props.onEvent} />
    )

    return (
        <StyledGrid itemProp="articleBody">
            <StyledCell>
                <Headline text={publication.heading} />
            </StyledCell>

            <StyledCell>
                <Byline
                    profile={publication.profile}
                    text={publication.byline}
                    isSponsored={publication.isSponsored}
                    timestamp={publication.publicationDate}
                    source={publication.source}
                    isLive={publication.isLive}
                    isFullWidth={true}
                    url={articleUrl}
                    title={publication.heading}
                    isPremium={isPremium}
                />
            </StyledCell>

            <EntitledToView
                isPremium={isPremium}
                articleSlug={publication.slug}
                entitled={() => {
                    if (props.heroOptions.isVideoStickyEnabled === true) {
                        return (
                            <React.Fragment>
                                <StyledStickyTop className="sticky-top" />
                                <StyledStickyCell className="sticky-cell">
                                    <ArticleHero
                                        heroVideo={publication.heroVideo}
                                        heroImage={heroImage}
                                        heading={publication.heading}
                                        onEvent={props.onEvent}
                                        showVideoTitles={true}
                                        isVideoStickyEnabled={true}
                                        getVideoQueue={props.getVideoQueue}
                                        publicationPathname={props.location.pathname}
                                        adUnitPath={props.adUnitPath}
                                    />
                                </StyledStickyCell>
                            </React.Fragment>
                        )
                    } else {
                        return (
                            <StyledCell>
                                <ArticleHero
                                    heroVideo={publication.heroVideo}
                                    heroImage={heroImage}
                                    heading={publication.heading}
                                    onEvent={props.onEvent}
                                    showVideoTitles={true}
                                    isVideoStickyEnabled={false}
                                    getVideoQueue={props.getVideoQueue}
                                    publicationPathname={props.location.pathname}
                                    adUnitPath={props.adUnitPath}
                                />
                            </StyledCell>
                        )
                    }
                }}
            />

            <StyledCell>
                <StyledNestedGrid>
                    <StyledAside>
                        <StyledSharing
                            url={articleUrl}
                            text={publication.heading}
                            onEvent={props.onEvent}
                            shareOptions={['facebook', 'twitter', 'email', 'clipboard']}
                        />
                    </StyledAside>
                    <div>
                        {content && (
                            <StyledArticleContent>
                                <ArticleBreach
                                    isPremium={isPremium}
                                    location={props.location}
                                    onEvent={props.onEvent}
                                    blocks={content}
                                    targetedContent={props.targetedContent}
                                    inlinePositionedContent={props.inlinePositionedContent}
                                    publicationId={publication.id}
                                    publicationHeadline={publication.heading}
                                    slug={publication.slug}
                                >
                                    {({ blocks, targetedContent, inlinePositionedContent }) => (
                                        <ArticleBlockContent
                                            blocks={blocks}
                                            targetedContentElement={
                                                targetedContent &&
                                                targetedContent.position === 'inline'
                                                    ? targetedContent.element
                                                    : undefined
                                            }
                                            inlinePositionedContent={inlinePositionedContent}
                                            onEvent={props.onEvent}
                                            log={props.log}
                                            publicationPathname={props.location.pathname}
                                            adUnitPath={props.adUnitPath}
                                            getVideoQueue={props.getVideoQueue}
                                        />
                                    )}
                                </ArticleBreach>

                                <EntitledToView
                                    isPremium={isPremium}
                                    entitled={() =>
                                        canonicalLink ? (
                                            <Footnote
                                                text="Originally published as "
                                                link={canonicalLink}
                                            />
                                        ) : null
                                    }
                                />

                                <LinkBanner
                                    url={`https://emails.thewest.com.au/subscribe/index.html?utm_source=${sectionMeta.Hostname ||
                                        'TheWest'}&utm_campaign=ArticleLinkBanner&utm_content=ArticleLinkBanner`}
                                    message="Get the latest news from thewest.com.au in your inbox."
                                    cta="Sign up for our emails"
                                    verticalGutters={['md', 'md']}
                                />
                            </StyledArticleContent>
                        )}
                    </div>
                </StyledNestedGrid>
            </StyledCell>

            {relatedItems && (
                <StyledFlexCell>
                    {renderUserTopicPublications}
                    <RelatedContent items={relatedItems} onEvent={props.onEvent} />
                </StyledFlexCell>
            )}

            <FeatureToggle
                feature="ripple"
                on={() => (
                    <StyledCell>
                        <RippleAd />
                    </StyledCell>
                )}
            />
        </StyledGrid>
    )
}
